import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { loadOwnPublicProfile, publicProfilePath } from "@/lib/publicProfile";

interface Props {
  username: string | null;
  userId: string | undefined;
}

export function ReferralLinkCard({ username, userId }: Props) {
  const [profilePublic, setProfilePublic] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    loadOwnPublicProfile()
      .then((p) => alive && setProfilePublic(!!p?.is_public))
      .catch(() => {});
    return () => { alive = false; };
  }, [userId]);

  const referralLink = username
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${username}`
    : "";

  const copy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-base">Your referral link</CardTitle>
      </CardHeader>
      <CardContent>
        {referralLink ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-muted px-3 py-2 text-xs font-mono text-foreground">
                {referralLink}
              </code>
              <Button onClick={copy} variant="outline" size="sm">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
            {username ? (
              profilePublic ? (
                <a
                  href={publicProfilePath(username)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-xs text-primary hover:underline"
                >
                  View your public profile
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              ) : (
                <p className="text-xs text-muted-foreground">
                  <Link to="/settings" className="text-primary hover:underline">
                    Publish a public profile
                  </Link>{" "}
                  so visitors landing on /u/{username} see who you are.
                </p>
              )
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Set a username in your profile to get a referral link.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
